import React, { useState } from 'react';
import toast from 'react-hot-toast';
import PageHeader from '../../components/PageHeader';
import { useAuth } from '../../hooks/useAuth';
import { ROLES, ROLE_LABEL } from '../../utils/roles';
import { ShieldCheck, Bell, Lock, Database, KeyRound, Globe, Eye, EyeOff } from 'lucide-react';
import * as authApi from '../../api/auth.api';
import { errorMessage } from '../../api/client';

const MIN_PASSWORD_LEN = 8;

function Section({ icon: Icon, title, description, children }) {
  return (
    <div className="card">
      <div className="card-title">
        <h3><Icon size={18} style={{ verticalAlign: 'middle', color: 'var(--brand-700)' }} /> {title}</h3>
      </div>
      {description && <div className="muted" style={{ marginBottom: 'var(--s-3)' }}>{description}</div>}
      {children}
    </div>
  );
}

function ChangePasswordModal({ open, onClose }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  function reset() {
    setCurrent(''); setNew(''); setConfirm(''); setErr(''); setShow(false);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    if (!currentPassword) { setErr('Enter your current password.'); return; }
    if (newPassword.length < MIN_PASSWORD_LEN) { setErr(`New password must be at least ${MIN_PASSWORD_LEN} characters.`); return; }
    if (newPassword === currentPassword) { setErr('New password must be different from your current password.'); return; }
    if (newPassword !== confirm) { setErr('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success('Password updated');
      reset();
      onClose();
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) { reset(); onClose(); } }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head"><h3>Change password</h3></div>
        <form onSubmit={onSubmit}>
          <div className="modal-body">
            <div className="field">
              <label>Current password</label>
              <input
                type={show ? 'text' : 'password'}
                className="input"
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrent(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>New password <span className="muted text-xs">(min {MIN_PASSWORD_LEN} chars)</span></label>
              <div className="row" style={{ gap: 8, alignItems: 'stretch' }}>
                <input
                  type={show ? 'text' : 'password'}
                  className="input"
                  style={{ flex: 1 }}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNew(e.target.value)}
                  required
                  minLength={MIN_PASSWORD_LEN}
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="btn btn-ghost"
                  aria-label={show ? 'Hide passwords' : 'Show passwords'}
                >
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="field">
              <label>Confirm new password</label>
              <input
                type={show ? 'text' : 'password'}
                className="input"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={MIN_PASSWORD_LEN}
              />
            </div>
            {err && <div className="notice warning" style={{ marginTop: 8 }}>{err}</div>}
          </div>
          <div className="modal-foot">
            <button type="button" className="btn btn-ghost" onClick={() => { reset(); onClose(); }} disabled={busy}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Updating…' : <><KeyRound size={14} /> Update password</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Settings() {
  const { user } = useAuth();
  const role = user?.role;
  const isSuper = role === ROLES.SUPER_ADMIN;
  const isAdmin = [ROLES.ADMIN, ROLES.STAFF].includes(role);
  const [pwOpen, setPwOpen] = useState(false);

  return (
    <>
      <PageHeader
        title="Settings"
        subtitle={`Configuration available to ${ROLE_LABEL[role] || role} accounts.`}
      />

      <div className="grid grid-2">
        <Section icon={Bell} title="Notifications" description="Choose how you want to be notified.">
          <div className="col">
            <label className="row between"><span>Email — order updates</span><input type="checkbox" defaultChecked /></label>
            <label className="row between"><span>Email — payment receipts</span><input type="checkbox" defaultChecked /></label>
          </div>
        </Section>

        <Section icon={Lock} title="Security" description="Manage your account password.">
          <div className="col">
            <button className="btn btn-outline" onClick={() => setPwOpen(true)}>
              <KeyRound size={14} /> Change password
            </button>
          </div>
        </Section>

        {isSuper && (
          <>
            <Section icon={ShieldCheck} title="System access" description="Super admin only — restricted controls.">
              <div className="col">
                <div className="list-row"><span>Audit log retention</span><span className="muted text-sm">90 days</span></div>
                <div className="list-row"><span>Rate limit window</span><span className="muted text-sm">15 min · 200 / IP</span></div>
                <div className="list-row"><span>Auth rate limit</span><span className="muted text-sm">15 min · 20 / IP</span></div>
              </div>
            </Section>

            <Section icon={Database} title="Backups" description="Database and uploads volume.">
              <div className="muted">
                Nightly backups run on the server. Contact your system administrator for restore requests.
              </div>
            </Section>

            <Section icon={Globe} title="Allowed origins" description="Frontends that can call the API.">
              <div className="muted">
                Update the allowed origins list in your server configuration, then restart the backend.
              </div>
            </Section>
          </>
        )}

        {isAdmin && (
          <Section icon={ShieldCheck} title="Limited access" description="Admin / staff scope.">
            <div className="muted">
              You don't have access to system-level settings or the audit log. Reach out to a Super Admin
              for changes to security policy, retention, or rate limits.
            </div>
          </Section>
        )}
      </div>

      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}

export default Settings;
